import React from "react";
import { Box, IconButton, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { FieldArray, useFormikContext } from "formik";
import HTMLEditor from "../../HtmlEditor/HtmlEditor";
import { Delete as DeleteIcon, Add } from "@mui/icons-material";
import { Select } from "../../FormikFields";
import OrderingList from "../../Lists/OrderingList";
import QuestionTextField from "./QuestionTextField";
import AnswersField from "./AnswersField";
import { PropTypes } from "prop-types";
import {
  getNewMatchClue,
  getNewMatchChoice
} from "../../../utils/courseBuilderUtils";
import { sortBy } from "lodash";

const matchingTextFieldToolbar =
  "undo redo | bold italic underline | alignment numlist bullist outdent indent | link image media fileUpload";

const updateMatchItemsPosition = matchItems => {
  matchItems.forEach((matchItem, index) => {
    matchItem.position = index;
  });
};

const useStyles = makeStyles(theme => ({
  answers: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0
  },
  matchingTextField: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1,
    maxWidth: "calc(100% - 96px)", // prevents html editor from growing too large when images/videos are added
    [theme.breakpoints.down("md")]: {
      maxWidth: "calc(100%)"
    }
  }
}));

const AddMatchItemButton = ({ label, onClick }) => (
  <Box my={2} ml={-1.5}>
    <Button
      color="primary"
      startIcon={<Add />}
      onClick={onClick}
      data-qa="add-match-item-btn"
    >
      {label}
    </Button>
  </Box>
);

const MatchingTextField = ({
  matchItem,
  onDelete,
  onEdit,
  placeholder,
  questionFilesList,
  setQuestionFilesList,
  disableDeleteBtn,
  error
}) => {
  const classes = useStyles();
  return matchItem ? (
    <Box
      className={classes.matchingTextField}
      data-qa={`matching-field-${matchItem.type}`}
    >
      <Box
        flex={1}
        minHeight="100%"
        maxWidth="100%"
        data-qa="matching-text-field"
      >
        <HTMLEditor
          disableMargin
          onChange={onEdit}
          value={matchItem.text}
          menubar={false}
          onFileAdd={(fileObj, tempLink, type) => {
            const list = questionFilesList;
            list.push({ fileObj, tempLink, type });
            setQuestionFilesList(list);
          }}
          plugins={["lists", "link", "image", "media", "code"]}
          toolbar={matchingTextFieldToolbar}
          inline={true}
          toolbarId={`toolbar-${matchItem.id}`}
          disableResize
          placeholder={placeholder}
          errorMessage={error}
        />
      </Box>
      <Box display="flex" alignItems="center" ml={1}>
        <IconButton
          onClick={() => onDelete(matchItem)}
          data-qa="matching-text-delete-btn"
          disabled={disableDeleteBtn}
          color="primary"
          size="large"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  ) : null;
};

const MatchSelectField = ({ clueIndex, matchChoices, onChange }) => {
  const menuItems = [...Array(matchChoices.length)].map((item, index) => ({
    label: index + 1,
    value: matchChoices[index]
  }));
  return (
    <Box width={80} data-qa="matching-select-field">
      <Select
        name={`matchClues[${clueIndex}].correctMatch`}
        menuItems={menuItems}
        renderValue={value => value.position + 1}
        onChange={onChange}
        hasEmptyItem={true}
      />
    </Box>
  );
};

const MatchingFields = ({
  isSurveyQuestion,
  questionFilesList,
  setQuestionFilesList
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    errors,
    touched,
    values: { matchClues, matchChoices },
    setFieldValue,
    submitCount,
    validateForm
  } = useFormikContext();

  const handleDeleteMatchChoice = deletedMatchChoiceIndex => deletedMatchChoice => {
    matchChoices.splice(deletedMatchChoiceIndex, 1);
    updateMatchItemsPosition(matchChoices);
    matchClues.forEach(matchClue => {
      if (
        matchClue.correctMatch &&
        matchClue.correctMatch.id === deletedMatchChoice.id
      ) {
        matchClue.correctMatch = "";
      }
    });
    setFieldValue("matchClues", matchClues);
    setFieldValue("matchChoices", matchChoices);
  };

  const handleDeleteMatchClue = deletedMatchClueIndex => () => {
    matchClues.splice(deletedMatchClueIndex, 1);
    updateMatchItemsPosition(matchClues);
    setFieldValue("matchClues", matchClues);
  };

  const handleMatchChange = matchClue => newCorrectMatch => {
    matchClue.correctMatch = newCorrectMatch;
    setFieldValue("matchClues", matchClues);
  };

  // gets the individual field error for the match clue or match choice at the specified index
  // We'll only show the error after the form has been submitted and if the field has been touched
  const getMatchFieldError = (itemType, itemIndex) => {
    return submitCount > 0 &&
      touched[itemType] &&
      touched[itemType][itemIndex] &&
      errors[itemType] &&
      errors[itemType][itemIndex]
      ? Object.values(errors[itemType][itemIndex])[0]
      : null;
  };

  return (
    <Box display="flex" flexDirection="column">
      <QuestionTextField
        questionFilesList={questionFilesList}
        setQuestionFilesList={setQuestionFilesList}
      />
      <AnswersField
        renderAnswers={() => (
          <Box display="flex" flexDirection="column" flex={1}>
            {!isSurveyQuestion && (
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 4.5 }}>
                {t("editQuestion.matchingQuestionNote")}
              </Typography>
            )}
            <Box
              display="flex"
              flex={1}
              flexWrap="wrap"
              flexDirection={{ xs: "column", md: "row" }}
            >
              <Box
                flex={5}
                minWidth={{ xs: "100%", md: "unset" }}
                className={classes.answers}
              >
                <FieldArray
                  name="matchClues"
                  render={({ replace, push }) => (
                    <Box
                      display="flex"
                      flexDirection="column"
                      flex={1}
                      pr={{ xs: 0, md: 6 }}
                    >
                      <OrderingList
                        listItems={matchClues}
                        renderText={(matchClue, index) => (
                          <MatchingTextField
                            onDelete={handleDeleteMatchClue(index)}
                            onEdit={newText => {
                              replace(index, { ...matchClue, text: newText });
                            }}
                            matchItem={matchClue}
                            value={matchClue.text}
                            placeholder={t("editQuestion.matchPlaceholder", {
                              index: index + 1
                            })}
                            questionFilesList={questionFilesList}
                            setQuestionFilesList={setQuestionFilesList}
                            disableDeleteBtn={matchClues.length <= 2}
                            error={getMatchFieldError("matchClues", index)}
                          />
                        )}
                        renderOrderField={(matchClue, index) =>
                          !isSurveyQuestion ? (
                            <MatchSelectField
                              matchChoices={matchChoices}
                              clueIndex={index}
                              onChange={handleMatchChange(matchClue)}
                            />
                          ) : null
                        }
                      />
                      <AddMatchItemButton
                        onClick={() =>
                          push(getNewMatchClue(matchClues.length, ""))
                        }
                        label={t("editQuestion.addMatchBtn")}
                      />
                    </Box>
                  )}
                />
              </Box>
              <Box
                minWidth={{ xs: "100%", md: "unset" }}
                flex={4}
                className={classes.answers}
              >
                <FieldArray
                  name="matchChoices"
                  render={({ push }) => (
                    <Box display="flex" flexDirection="column" flex={1}>
                      <OrderingList
                        listItems={sortBy(matchChoices, ["position"])}
                        renderText={(matchChoice, index) => (
                          <MatchingTextField
                            onDelete={handleDeleteMatchChoice(index)}
                            onEdit={newText => {
                              matchChoice.text = newText;
                              validateForm();
                            }}
                            itemIndex={index}
                            matchItem={matchChoice}
                            value={matchChoice.text}
                            placeholder={t(
                              "editQuestion.matchChoicePlaceholder",
                              {
                                index: index + 1
                              }
                            )}
                            questionFilesList={questionFilesList}
                            setQuestionFilesList={setQuestionFilesList}
                            disableDeleteBtn={matchChoices.length <= 2}
                            error={getMatchFieldError("matchChoices", index)}
                          />
                        )}
                        renderOrderField={(item, index) => (
                          <Typography variant="subtitle1">{`${index +
                            1}.`}</Typography>
                        )}
                      />
                      <AddMatchItemButton
                        onClick={() =>
                          push(getNewMatchChoice(matchChoices.length))
                        }
                        label={t("editQuestion.addChoiceBtn")}
                      />
                    </Box>
                  )}
                />
              </Box>
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};

MatchingFields.propTypes = {
  isSurveyQuestion: PropTypes.bool,
  questionFilesList: PropTypes.array,
  setQuestionFilesList: PropTypes.func
};

export default MatchingFields;
